import type { UTCDate } from '@date-fns/utc';

import type { PostingNode } from '@revelio/data-access';

import { PAY_FILTER_MAX } from '../../../filters/definitions/pay';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
} from '../../../filters/types/filters';
import { isRangeFilter } from '../filter-type-guards';
import { buildRangeNode, wrapExprNode } from './node-builders';
import { PostingDiscoveryTags } from './tags';

const DEFAULT_MIN_SALARY = 0;
const DEFAULT_MAX_SALARY = PAY_FILTER_MAX;

export const buildSalaryFilter = (filters: Filter[]): PostingNode | null => {
  const payFilter = findFilterById(filters, FilterId.PAY);
  if (!payFilter || payFilter.type !== FilterType.RANGE) {
    return null;
  }

  const { min, max, isUserSubmitted } = payFilter;

  if (!isUserSubmitted) {
    return null;
  }

  if (min === undefined && max === undefined) {
    return null;
  }

  const start = min ?? DEFAULT_MIN_SALARY;
  const end = max ?? DEFAULT_MAX_SALARY;

  const exprNode = buildRangeNode(PostingDiscoveryTags.Salary, start, end);
  return wrapExprNode(exprNode, payFilter.isNot);
};

export const buildMultiSalaryFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiPay = findFilterById(filters, FilterId.MULTI_PAY);
  if (!multiPay || !isMultiFilter(multiPay)) {
    return null;
  }
  if (multiPay.instances.length === 0) {
    return null;
  }

  const instanceNodes: PostingNode[] = [];
  for (const instance of multiPay.instances) {
    if (!isRangeFilter(instance)) continue;

    const { min, max, isUserSubmitted, isNot } = instance;
    if (!isUserSubmitted) continue;
    if (min === undefined && max === undefined) continue;

    const start = min ?? DEFAULT_MIN_SALARY;
    const end = max ?? DEFAULT_MAX_SALARY;

    const exprNode = buildRangeNode(PostingDiscoveryTags.Salary, start, end);
    instanceNodes.push(wrapExprNode(exprNode, isNot));
  }

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }
  return instanceNodes;
};

// POST_DATE range start/end are a day index from 2008-01-01 (UTC), inclusive —
// the same system-timeline origin composition_v3 uses for its month index.
const POST_DATE_EPOCH_UTC = Date.UTC(2008, 0, 1);
const MS_PER_DAY = 86_400_000;
// A manual week-picker selection is a week-start; pad it to include the whole
// week. AI-resolved ranges set `exactEnd` and are not padded (see below).
const END_WEEK_PADDING_DAYS = 6;
// Open-ended upper bound (well within Int32; far past any real post date).
const POST_DATE_MAX_INDEX = 100_000;

const toPostDateDayIndex = (date: UTCDate): number =>
  Math.floor(
    (Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) -
      POST_DATE_EPOCH_UTC) /
      MS_PER_DAY
  );

export const buildPostDateFilter = (filters: Filter[]): PostingNode | null => {
  const postDate = findFilterById(filters, FilterId.POST_DATE);
  if (!postDate || postDate.type !== FilterType.DATE_RANGE) {
    return null;
  }

  const { min, max } = postDate;
  if (!min && !max) {
    return null;
  }

  // Clamp the start to the index origin (2008-01-01 = index 0); never go before
  // it. Keep the end at or after the start.
  const startIndex = min ? Math.max(0, toPostDateDayIndex(min)) : 0;
  // Manual week-picker bounds get padded to cover the whole week; an AI-resolved
  // range is already an exact inclusive day, so it is not padded.
  const endPadding = postDate.exactEnd ? 0 : END_WEEK_PADDING_DAYS;
  const rawEndIndex = max
    ? toPostDateDayIndex(max) + endPadding
    : POST_DATE_MAX_INDEX;
  const endIndex = Math.max(startIndex, rawEndIndex);

  const exprNode = buildRangeNode(
    PostingDiscoveryTags.PostDate,
    startIndex,
    endIndex
  );
  return wrapExprNode(exprNode);
};
